import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'
import ModalContext from 'contexts/ModalContext'
import Modal from 'components/Luxkit/Modal/Modal'
import { fillArray, groupBy, sortBy, take } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'
import switchFunc from 'lib/function/switchFunc'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import LineBedBunkIcon from 'components/Luxkit/Icons/line/LineBedBunkIcon'
import LineBedSingleIcon from 'components/Luxkit/Icons/line/LineBedSingleIcon'
import LineBedQueenIcon from 'components/Luxkit/Icons/line/LineBedQueenIcon'
import LineBedDoubleAltIcon from 'components/Luxkit/Icons/line/LineBedDoubleAltIcon'
import LineBedKingIcon from 'components/Luxkit/Icons/line/LineBedKingIcon'
import LineBedSofaIcon from 'components/Luxkit/Icons/line/LineBedSofaIcon'
import SolidCheckCircleIcon from 'components/Luxkit/Icons/solid/SolidCheckCircleIcon'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'

const AmenitiesGrid = styled.div`
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: repeat(2, minmax(0, 280px));
  grid-gap: ${rem(16)};
`

const BedroomGrid = styled.div`
  display: grid;
  gap: ${rem(20)};
  grid-template-columns: 1fr 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const ServiceItem = styled.div`
  padding: ${rem(16)} 0;
  display: flex;
  gap: ${rem(12)};
`

const HighlightTile = styled(VerticalSpacer)`
  width: ${rem(200)};

  ${mediaQueryUp.desktop} {
    width: auto;
  }

  > picture {
    max-height: ${rem(180)};
  }
`

const BedroomTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: ${rem(160)};
  padding: ${rem(12)} ${rem(20)};
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  border-radius: ${(props) => props.theme.borderRadius.medium};
`

const bedroomTypeToIcon = switchFunc<React.ReactNode, App.VillaRoomBedType>({
  QUEEN_SIZE_BED: <LineBedQueenIcon size="XL" />,
  SINGLE_BED: <LineBedSingleIcon size="XL" />,
  BUNK_BED: <LineBedBunkIcon size="XL" />,
  DOUBLE_BED: <LineBedDoubleAltIcon size="XL" />,
  KING_SIZE_BED: <LineBedKingIcon size="XL" />,
  SOFABED: <LineBedSofaIcon size="XL" />,
  DOUBLE_SOFA_BED: <LineBedSofaIcon size="XL" />,
  TRIBUNK_BED: <LineBedBunkIcon size="XL" />,
  BUNK_BEDS_SGL_ON_TOP_DBL_ON_BOTTOM: <LineBedBunkIcon size="XL" />,
}, <LineBedIcon size="XL" />)

interface Props {
  offer: App.VillaOffer;
}

function VillasPageRoomsAndAmenities(props: Props) {
  const {
    offer,
  } = props

  const showModal = useContext(ModalContext)
  // villas only have 1 'room' type
  const amenities = useMemo(() => sortBy(offer.packages?.[0]?.roomType?.amenities ?? [], amenity => amenity.name), [offer])
  const openAmenitiesModal = useCallback(() => {
    const amenitiesByGroup = groupBy(amenities, amenity => amenity.group)

    showModal(
      <Modal title="Amenities">
        <VerticalSpacer gap={40}>
          {[...amenitiesByGroup.entries()].map(([groupName, amenities]) => (
            <VerticalSpacer gap={16} key={groupName}>
              <Heading variant="heading5">{groupName}</Heading>
              <AmenitiesGrid>
                {amenities.map((amenity) => (
                  <BodyTextBlock
                    key={amenity.name}
                    variant="medium"
                    format="titlecase"
                    startIcon={<LineCheckIcon />}
                  >
                    {amenity.name}
                  </BodyTextBlock>
                ))}
              </AmenitiesGrid>
            </VerticalSpacer>
          ))}
        </VerticalSpacer>
      </Modal>,
    )
  }, [showModal, amenities])

  const roomsWithBeds = useMemo(() => offer.property.rooms.filter(room => room.beds.length > 0), [offer])

  return (
    <Group fullWidth direction="vertical" gap={32} tabletGap={40}>
      <Heading variant="heading2">Rooms & Amenities</Heading>
      {offer.property.rooms.length > 0 && (
        <BedroomGrid>
          {roomsWithBeds.map((bedroom, index) => (
            <BedroomTile key={index}>
              <Group direction="horizontal" gap={4}>
                {bedroom.beds.flatMap(bed => fillArray(bed.count, () => bed.type)).map((type, index) => <React.Fragment key={index}>
                  {bedroomTypeToIcon(type)}
                </React.Fragment>)}
              </Group>
              <div>
                <BodyText variant="large" weight="bold" colour="neutral-two">
                  {bedroom.name} {index + 1}
                </BodyText>
                <BodyText variant="medium" colour="neutral-two">
                  {bedroom.beds.map(bed => pluralizeToString(bed.name, bed.count)).join(', ')}
                </BodyText>
              </div>
            </BedroomTile>
          ))}
        </BedroomGrid>
      )}
      {!!offer.property.highlights.length && <Group direction="vertical" gap={12}>
        <Heading variant="heading4">Highlights</Heading>
        <CardCarousel gap={12} pageSize={3}>
          {offer.property.highlights.map((highlight) => <HighlightTile gap={12} key={highlight.id}>
            <Image image={highlight.image} width={180} height={180} fit="center"/>
            <div>
              <BodyText variant="large" weight="bold" colour="neutral-two">{highlight.title}</BodyText>
              <BodyText variant="medium" colour="neutral-two">{highlight.description}</BodyText>
            </div>
          </HighlightTile>,
          )}
        </CardCarousel>
      </Group>}
      {!!amenities.length && (
        <VerticalSpacer gap={20}>
          <Heading variant="heading4">Amenities</Heading>
          <AmenitiesGrid>
            {take(amenities, 10).map((amenity) => (
              <BodyTextBlock
                key={amenity.name}
                variant="medium"
                format="titlecase"
                startIcon={<LineCheckIcon />}
              >
                {amenity.name}
              </BodyTextBlock>
            ))}
          </AmenitiesGrid>
          {amenities.length > 10 && <TextButton kind="secondary" onClick={openAmenitiesModal}>
            View all {amenities.length} amenities
          </TextButton>}
        </VerticalSpacer>
      )}
      {offer.property.inclusions.length > 0 && (
        <VerticalSpacer gap={8}>
          <Heading variant="heading4">
            Enjoy your stay with our <Heading variant="heading4" as="span" colour="exclusive">VIP services</Heading>
          </Heading>
          <ServicesGrid>
            {offer.property.inclusions.map((inclusion) => <ServiceItem key={inclusion.title}>
              <SolidCheckCircleIcon colour="exclusive" />
              <VerticalSpacer gap={4}>
                <BodyText variant="medium" weight="bold" colour="neutral-two">{inclusion.title}</BodyText>
                {inclusion.description && <BodyText variant="medium" colour="neutral-two">{inclusion.description}</BodyText>}
              </VerticalSpacer>
            </ServiceItem>)}
          </ServicesGrid>
        </VerticalSpacer>
      )}
    </Group>
  )
}

export default VillasPageRoomsAndAmenities
